import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react'
import type {
  SessionSummary,
  SessionDetail,
  SessionGrades,
  SessionAnalysis,
  ClassSummary,
} from '../api/types'
import { useDemoData } from '../demo/useDemoData'
import type { StudentSummary } from '../api/types'

/* A single graded paper row (element of SessionGrades). */
type GradeRow = SessionGrades[number]

/* A captured page image, kept as an object URL for the UI phase. */
export interface ScannedPage {
  id: string
  url: string
}

interface MobileDataValue {
  exams: SessionSummary[]
  classes: ClassSummary[]
  className: (id?: number | null) => string

  getDetail: (examId: number) => SessionDetail | undefined
  getAnalysis: (examId: number) => SessionAnalysis | undefined
  getGrades: (examId: number) => GradeRow[]
  getPaper: (examId: number, paperId: number) => GradeRow | undefined

  addExam: (exam: SessionSummary) => void

  /* Review editing (in-memory, UI phase only) */
  setQuestionScore: (examId: number, paperId: number, gradeId: number, score: number) => void
  setQuestionFeedback: (examId: number, paperId: number, gradeId: number, feedback: string) => void
  approvePaper: (examId: number, paperId: number) => void
  isPaperApproved: (paperId: number) => boolean

  /* Captured pages per paper */
  pagesFor: (paperId: number) => ScannedPage[]
  addPage: (paperId: number, page: ScannedPage) => void
  removePage: (paperId: number, pageId: string) => void

  /* Students by class */
  studentsForClass: (classId?: number | null) => StudentSummary[]
}

const MobileDataContext = createContext<MobileDataValue | null>(null)

export function useMobileData(): MobileDataValue {
  const ctx = useContext(MobileDataContext)
  if (!ctx) throw new Error('useMobileData must be used within MobileDataProvider')
  return ctx
}

/* deep clone helper so edits never mutate the shared demo fixtures */
function cloneGrades(src: Record<number, SessionGrades>): Record<number, GradeRow[]> {
  return JSON.parse(JSON.stringify(src))
}

export function MobileDataProvider({ children }: { children: ReactNode }) {
  const data = useDemoData()
  const [exams, setExams] = useState<SessionSummary[]>(data.exams)
  const [grades, setGrades] = useState<Record<number, GradeRow[]>>(() => cloneGrades(data.examGrades))
  const [approved, setApproved] = useState<Set<number>>(new Set())
  const [pages, setPages] = useState<Record<number, ScannedPage[]>>({})

  // Re-seed when the language (and therefore the fixture set) changes.
  useEffect(() => {
    setExams(data.exams)
    setGrades(cloneGrades(data.examGrades))
  }, [data])

  const classNameMap = useMemo(() => {
    const map = new Map<number, string>()
    data.classes.forEach((c) => map.set(c.id, c.name))
    return map
  }, [data.classes])

  const className = useCallback(
    (id?: number | null) => (id != null ? classNameMap.get(id) ?? '—' : '—'),
    [classNameMap],
  )

  const getDetail = useCallback((examId: number) => data.examDetails[examId], [data.examDetails])
  const getAnalysis = useCallback((examId: number) => data.examAnalysis[examId], [data.examAnalysis])
  const getGrades = useCallback((examId: number) => grades[examId] ?? [], [grades])
  const getPaper = useCallback(
    (examId: number, paperId: number) => (grades[examId] ?? []).find((p) => p.paper_id === paperId),
    [grades],
  )

  const addExam = useCallback((exam: SessionSummary) => {
    setExams((prev) => [exam, ...prev])
  }, [])

  const mutateRow = useCallback(
    (examId: number, paperId: number, fn: (row: GradeRow) => void) => {
      setGrades((prev) => {
        const rows = prev[examId]
        if (!rows) return prev
        const next = rows.map((r) => {
          if (r.paper_id !== paperId) return r
          const copy: GradeRow = JSON.parse(JSON.stringify(r))
          fn(copy)
          // recompute paper total from question scores
          if (copy.paper_grade) {
            copy.paper_grade.total_score = copy.question_grades.reduce((s, q) => s + q.score, 0)
          }
          return copy
        })
        return { ...prev, [examId]: next }
      })
    },
    [],
  )

  const setQuestionScore = useCallback(
    (examId: number, paperId: number, gradeId: number, score: number) => {
      mutateRow(examId, paperId, (row) => {
        const q = row.question_grades.find((g) => g.id === gradeId)
        if (q) q.score = Math.max(0, Math.min(q.max_score, score))
      })
    },
    [mutateRow],
  )

  const setQuestionFeedback = useCallback(
    (examId: number, paperId: number, gradeId: number, feedback: string) => {
      mutateRow(examId, paperId, (row) => {
        const q = row.question_grades.find((g) => g.id === gradeId)
        if (q) q.feedback = feedback
      })
    },
    [mutateRow],
  )

  const approvePaper = useCallback((_examId: number, paperId: number) => {
    setApproved((prev) => new Set(prev).add(paperId))
  }, [])

  const isPaperApproved = useCallback((paperId: number) => approved.has(paperId), [approved])

  const pagesFor = useCallback((paperId: number) => pages[paperId] ?? [], [pages])
  const addPage = useCallback((paperId: number, page: ScannedPage) => {
    setPages((prev) => ({ ...prev, [paperId]: [...(prev[paperId] ?? []), page] }))
  }, [])
  const removePage = useCallback((paperId: number, pageId: string) => {
    setPages((prev) => {
      const list = prev[paperId]
      if (!list) return prev
      const target = list.find((p) => p.id === pageId)
      if (target) URL.revokeObjectURL(target.url)
      return { ...prev, [paperId]: list.filter((p) => p.id !== pageId) }
    })
  }, [])

  const studentsForClass = useCallback(
    (classId?: number | null) => (classId != null ? data.studentsByClass[classId] ?? [] : []),
    [data.studentsByClass],
  )

  const value: MobileDataValue = {
    exams,
    classes: data.classes,
    className,
    getDetail,
    getAnalysis,
    getGrades,
    getPaper,
    addExam,
    setQuestionScore,
    setQuestionFeedback,
    approvePaper,
    isPaperApproved,
    pagesFor,
    addPage,
    removePage,
    studentsForClass,
  }

  return <MobileDataContext.Provider value={value}>{children}</MobileDataContext.Provider>
}
